// src/components/ComparisonTab.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Plot from 'react-plotly.js';
import {
  XMarkIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  FolderIcon,
  DocumentIcon,
  ArrowLeftIcon,
  UserIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';

// ============================================================================
// STATION COVERAGE VISUALIZATION
// ============================================================================
const StationCoverageVisualization = ({ 
  selectedSessions, 
  sessions, 
  sessionStats,
  SESSION_COLORS 
}) => {
  const formatStation = (feet) => {
    if (feet == null) return '—';
    const hundreds = Math.floor(feet / 100);
    const remainder = feet % 100;
    return `${hundreds}+${remainder.toFixed(0).padStart(2, '0')}`;
  };

  const getSessionDate = (session) => {
    return session?.run_date || session?.created_at || session?.upload_timestamp;
  };

  // Get selected sessions in date order with their stats
  const sessionsWithStats = useMemo(() => {
    return selectedSessions
      .map(id => {
        const session = sessions.find(s => s.session_id === id);
        const stats = sessionStats[id];
        return { id, session, stats };
      })
      .filter(item => item.session && item.stats)
      .sort((a, b) => {
        const dateA = new Date(getSessionDate(a.session) || 0);
        const dateB = new Date(getSessionDate(b.session) || 0);
        return dateA - dateB;
      });
  }, [selectedSessions, sessions, sessionStats]);

  // Calculate global min/max across all selected sessions
  const { globalMin, globalMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    sessionsWithStats.forEach(({ stats }) => {
      if (stats.minStation != null && stats.minStation < min) min = stats.minStation;
      if (stats.maxStation != null && stats.maxStation > max) max = stats.maxStation;
    });

    if (min === Infinity || max === -Infinity) return { globalMin: null, globalMax: null };
    return { globalMin: min, globalMax: max };
  }, [sessionsWithStats]);

  // Check if we have valid station data
  const hasValidData = sessionsWithStats.some(
    ({ stats }) => stats.minStation != null && stats.maxStation != null
  );

  if (!hasValidData || selectedSessions.length < 2) {
    return null;
  }

  const range = globalMax - globalMin;

  return (
    <div className="px-4 py-3 border-b border-gray-200 bg-white">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          Station Coverage
        </h4>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{formatStation(globalMin)}</span>
          <span>→</span>
          <span>{formatStation(globalMax)}</span>
        </div>
      </div>
      
      <div className="space-y-1.5">
        {sessionsWithStats.map(({ id, session, stats }, index) => {
          const color = SESSION_COLORS[index % SESSION_COLORS.length];
          const hasStations = stats.minStation != null && stats.maxStation != null;
          
          // Calculate position and width as percentages
          const leftPercent = hasStations ? ((stats.minStation - globalMin) / range) * 100 : 0;
          const widthPercent = hasStations ? ((stats.maxStation - stats.minStation) / range) * 100 : 0;

          return (
            <div key={id} className="flex items-center gap-2">
              {/* Session indicator */}
              <div 
                className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                style={{ backgroundColor: color }}
              >
                {index + 1}
              </div>
              
              {/* Bar container */}
              <div className="flex-1 h-5 bg-gray-100 rounded-sm relative overflow-hidden">
                {hasStations ? (
                  <>
                    {/* Coverage bar */}
                    <div
                      className="absolute top-0 bottom-0 rounded-sm transition-all duration-300"
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`,
                        backgroundColor: color,
                        opacity: 0.7,
                      }}
                    />
                    {/* Station labels inside bar */}
                    <div 
                      className="absolute top-0 bottom-0 flex items-center justify-between px-1 text-xs font-medium pointer-events-none"
                      style={{
                        left: `${leftPercent}%`,
                        width: `${widthPercent}%`,
                        color: widthPercent > 20 ? 'white' : 'transparent',
                      }}
                    >
                      <span className="truncate">{formatStation(stats.minStation)}</span>
                      {widthPercent > 40 && (
                        <span className="truncate">{formatStation(stats.maxStation)}</span>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
                    No station data
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Alignment indicator */}
      {sessionsWithStats.length >= 2 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          {(() => {
            // Check alignment
            const firstSession = sessionsWithStats[0];
            const hasOffset = sessionsWithStats.some(({ stats }) => 
              stats.minStation != null && 
              firstSession.stats.minStation != null &&
              Math.abs(stats.minStation - firstSession.stats.minStation) > 50
            );
            const hasDifferentEnd = sessionsWithStats.some(({ stats }) =>
              stats.maxStation != null &&
              firstSession.stats.maxStation != null &&
              Math.abs(stats.maxStation - firstSession.stats.maxStation) > 50
            );

            if (hasOffset || hasDifferentEnd) {
              return (
                <div className="flex items-start gap-1.5 text-xs text-amber-600">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    {hasOffset && hasDifferentEnd 
                      ? 'Sessions have different start and end points'
                      : hasOffset 
                        ? 'Sessions start at different stations'
                        : 'Sessions end at different stations'}
                  </span>
                </div>
              );
            }
            return (
              <div className="flex items-center gap-1.5 text-xs text-green-600">
                <CheckIcon className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Station ranges align well</span>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// IRI OVERLAY COMPARISON CHART
// ============================================================================
const ComparisonChart = React.memo(({
  orderedSessions,
  sessionData,
  sessions,
  getSessionInfo,
  getSessionColor,
  getSessionDate,
  formatStation,
  formatDate,
  SESSION_COLORS,
  projectConfig,
  iriThreshold,
  thresholdType,
}) => {
  const { traces, xRange, yRange, tickVals, tickText } = useMemo(() => {
    const allTraces = [];
    let globalMin = Infinity;
    let globalMax = -Infinity;
    let yMax = 0;

    orderedSessions.forEach((sessionId, index) => {
      const data = sessionData[sessionId];
      if (!data?.segments) return;

      const segs = data.segments
        .filter(s => !s.exception && s.iri_avg_in_per_mi != null)
        .sort((a, b) => a.from_station_ft - b.from_station_ft);

      if (segs.length === 0) return;

      const x = [];
      const y = [];
      const customdata = [];

      segs.forEach(seg => {
        x.push(seg.from_station_ft);
        y.push(seg.iri_avg_in_per_mi);
        customdata.push(formatStation(seg.from_station_ft));

        const staMin = Math.min(seg.from_station_ft, seg.to_station_ft);
        const staMax = Math.max(seg.from_station_ft, seg.to_station_ft);
        if (staMin < globalMin) globalMin = staMin;
        if (staMax > globalMax) globalMax = staMax;
        if (seg.iri_avg_in_per_mi > yMax) yMax = seg.iri_avg_in_per_mi;
      });

      const info = getSessionInfo(sessionId);
      const color = getSessionColor(sessionId);
      const date = formatDate(getSessionDate(info));

      allTraces.push({
        x, y, customdata,
        type: 'scatter',
        mode: 'lines',
        name: `${info?.name || 'Session'} (${date})`,
        line: { color, width: 1.5, shape: 'spline' },
        hovertemplate: '<b>Sta:</b> %{customdata}<br><b>MRI:</b> %{y:.1f} in/mi<extra>%{fullData.name}</extra>',
      });
    });

    // Tick generation
    const span = globalMax - globalMin;
    const step = span > 5000 ? 1000 : span > 2000 ? 500 : span > 500 ? 100 : 50;
    const vals = [];
    const texts = [];
    for (let v = Math.ceil(globalMin / step) * step; v <= globalMax; v += step) {
      vals.push(v);
      texts.push(formatStation(v));
    }

    const yPad = yMax * 0.15;

    return {
      traces: allTraces,
      xRange: [globalMin, globalMax],
      yRange: [0, yMax + yPad],
      tickVals: vals,
      tickText: texts,
    };
  }, [orderedSessions, sessionData, getSessionInfo, getSessionColor, getSessionDate, formatStation, formatDate]);

  // Threshold line — reflects the user-controlled Fixed Interval Threshold
  // (only meaningful when the threshold mode is a constant MRI limit)
  const thresholdValue = thresholdType === 'constant' && iriThreshold > 0 ? iriThreshold : null;

  const layout = useMemo(() => ({
    xaxis: {
      title: { text: 'Station', font: { size: 9, color: '#9ca3af' } },
      showgrid: true, gridcolor: '#e5e7eb',
      tickmode: 'array', tickvals: tickVals, ticktext: tickText, tickangle: -45,
      tickfont: { size: 8 },
      range: xRange,
      autorange: false,
      showspikes: false,
    },
    yaxis: {
      title: { text: 'MRI (in/mi)', font: { size: 10 } },
      showgrid: true, gridcolor: '#e5e7eb',
      range: yRange,
      autorange: false,
      fixedrange: true,
    },
    showlegend: false,
    margin: { l: 50, r: 15, t: 5, b: 50 },
    hovermode: 'x',
    autosize: true,
    plot_bgcolor: '#fafafa',
    paper_bgcolor: '#ffffff',
    dragmode: 'zoom',
    shapes: thresholdValue ? [{
      type: 'line', xref: 'paper', yref: 'y',
      x0: 0, x1: 1, y0: thresholdValue, y1: thresholdValue,
      line: { color: '#ef4444', width: 1.5, dash: 'dash' },
    }] : [],
  }), [tickVals, tickText, xRange, yRange, thresholdValue]);

  const config = useMemo(() => ({
    displayModeBar: true, displaylogo: false, responsive: true, scrollZoom: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d', 'pan2d'],
  }), []);

  // Pre-sort segments per session for fast binary lookup on hover
  const sortedSegmentsMap = useMemo(() => {
    const map = {};
    orderedSessions.forEach(sessionId => {
      const data = sessionData[sessionId];
      if (!data?.segments) { map[sessionId] = []; return; }
      map[sessionId] = data.segments
        .filter(s => !s.exception && s.iri_avg_in_per_mi != null)
        .sort((a, b) => a.from_station_ft - b.from_station_ft);
    });
    return map;
  }, [orderedSessions, sessionData]);

  // Refs for DOM-based hover
  const hoverLineRef = useRef(null);
  const hoverStaRef = useRef(null);
  const hoverSegRef = useRef(null);
  const hoverValueRefs = useRef({});
  const hoverDotRefs = useRef({});
  const plotRef = useRef(null);
  const chartContainerRef = useRef(null);

  const findIriAtStation = useCallback((segs, station) => {
    if (!segs || segs.length === 0) return { iri: null, index: -1 };
    let lo = 0, hi = segs.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (segs[mid].from_station_ft < station) lo = mid + 1;
      else hi = mid - 1;
    }
    const idx = lo < segs.length ? lo : segs.length - 1;
    const prev = idx > 0 ? idx - 1 : 0;
    const closest = Math.abs(segs[prev].from_station_ft - station) < Math.abs(segs[idx]?.from_station_ft - station) ? prev : idx;
    return { iri: segs[closest]?.iri_avg_in_per_mi ?? null, index: closest };
  }, []);

  if (traces.length === 0) return null;

  return (
    <div className="border-b border-gray-200">
      {/* Header with live hover stats */}
      <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Segment MRI</h3>
          <span ref={hoverStaRef} className="text-xs font-mono tabular-nums text-gray-500 w-20">—</span>
        </div>
        <div className="flex items-center gap-4 text-xs font-mono tabular-nums">
          <span className="flex items-center gap-1.5">
            <span className="text-xs text-gray-400">Seg</span>
            <span ref={hoverSegRef} className="text-xs font-semibold text-gray-700 w-8">—</span>
          </span>
          <span className="w-px h-4 bg-gray-300" />
          {orderedSessions.map((sessionId) => {
            const color = getSessionColor(sessionId);
            const info = getSessionInfo(sessionId);
            const date = formatDate(getSessionDate(info));
            return (
              <span key={sessionId} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                <span className="text-gray-500">{date}</span>
                <span ref={el => hoverValueRefs.current[sessionId] = el} className="font-semibold text-gray-700 w-10">—</span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Chart with hover line */}
      <div
        ref={chartContainerRef}
        className="relative"
        style={{ height: '360px' }}
        onMouseLeave={() => {
          if (hoverLineRef.current) hoverLineRef.current.style.opacity = '0';
          if (hoverStaRef.current) hoverStaRef.current.textContent = '—';
          if (hoverSegRef.current) hoverSegRef.current.textContent = '—';
          orderedSessions.forEach(id => {
            if (hoverValueRefs.current[id]) hoverValueRefs.current[id].textContent = '—';
            if (hoverDotRefs.current[id]) hoverDotRefs.current[id].style.opacity = '0';
          });
        }}
      >
        <div
          ref={hoverLineRef}
          className="absolute top-0 bottom-0 w-px bg-blue-400/50 pointer-events-none z-10"
          style={{ opacity: 0 }}
        />
        {/* Hover dots per session */}
        {orderedSessions.map(sessionId => (
          <div
            key={sessionId}
            ref={el => hoverDotRefs.current[sessionId] = el}
            className="absolute w-3 h-3 rounded-full border-2 border-white pointer-events-none z-10"
            style={{ backgroundColor: getSessionColor(sessionId), opacity: 0, transform: 'translate(-50%, -50%)' }}
          />
        ))}
        <style>{`.js-plotly-plot .plotly .hoverlayer { display: none !important; }`}</style>
        <Plot
          ref={plotRef}
          data={traces}
          layout={{ ...layout, hovermode: 'x' }}
          config={config}
          style={{ width: '100%', height: '100%' }}
          useResizeHandler
          onHover={(data) => {
            if (!data?.points?.[0]) return;
            const point = data.points[0];
            const station = point.x;

            // Snap hover line to data point pixel position
            const plotEl = plotRef.current?.el;
            if (plotEl && chartContainerRef.current && hoverLineRef.current) {
              const xaxis = plotEl._fullLayout?.xaxis;
              if (xaxis && typeof xaxis.d2p === 'function') {
                const plotContainer = plotEl.querySelector('.plot-container') || plotEl;
                const plotContainerRect = plotContainer.getBoundingClientRect();
                const containerRect = chartContainerRef.current.getBoundingClientRect();
                const px = xaxis.d2p(station) + (xaxis._offset || 0) + (plotContainerRect.left - containerRect.left);
                hoverLineRef.current.style.left = `${px}px`;
                hoverLineRef.current.style.opacity = '1';
              }
            }

            if (hoverStaRef.current) hoverStaRef.current.textContent = formatStation(station);

            // Get plot axes for coordinate conversion
            const xaxis = plotEl?._fullLayout?.xaxis;
            const yaxis = plotEl?._fullLayout?.yaxis;
            const plotArea = plotEl?.querySelector('.draglayer');
            const containerRect = chartContainerRef.current?.getBoundingClientRect();
            const plotRect = plotArea?.getBoundingClientRect();

            let segIndex = -1;
            orderedSessions.forEach(id => {
              const { iri, index } = findIriAtStation(sortedSegmentsMap[id], station);
              if (hoverValueRefs.current[id]) {
                hoverValueRefs.current[id].textContent = iri != null ? iri.toFixed(1) : '—';
              }
              if (index >= 0 && segIndex < 0) segIndex = index;

              // Position dot using Plotly's internal axis mapping
              const dot = hoverDotRefs.current[id];
              if (dot && iri != null && xaxis && yaxis && containerRect) {
                const seg = sortedSegmentsMap[id][index];
                // d2p returns pixels relative to the plot area origin
                // xaxis._offset is the left edge of the plot area in the SVG
                // yaxis._offset is the top edge of the plot area in the SVG
                const svgX = xaxis.d2p(seg.from_station_ft) + (xaxis._offset || 0);
                const svgY = yaxis.d2p(iri) + (yaxis._offset || 0);
                // The SVG is positioned at the top-left of the Plot container
                const plotContainer = plotEl.querySelector('.plot-container') || plotEl;
                const plotContainerRect = plotContainer.getBoundingClientRect();
                const px = svgX + (plotContainerRect.left - containerRect.left);
                const py = svgY + (plotContainerRect.top - containerRect.top);
                dot.style.left = `${px}px`;
                dot.style.top = `${py}px`;
                dot.style.opacity = '1';
              } else if (dot) {
                dot.style.opacity = '0';
              }
            });

            if (hoverSegRef.current) hoverSegRef.current.textContent = segIndex >= 0 ? `#${segIndex + 1}` : '—';
          }}
          onUnhover={() => {
            if (hoverLineRef.current) hoverLineRef.current.style.opacity = '0';
            if (hoverStaRef.current) hoverStaRef.current.textContent = '—';
            if (hoverSegRef.current) hoverSegRef.current.textContent = '—';
            orderedSessions.forEach(id => {
              if (hoverValueRefs.current[id]) hoverValueRefs.current[id].textContent = '—';
              if (hoverDotRefs.current[id]) hoverDotRefs.current[id].style.opacity = '0';
            });
          }}
        />
      </div>
    </div>
  );
});

// ============================================================================
// SESSION SELECTION MODAL - With Folder Navigation
// ============================================================================
const SessionSelectionModal = ({
  isOpen,
  onClose,
  sessions = [],
  folders = [],
  selectedSessions = [],
  onSelectedSessionsChange,
  sessionStats = {},
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentFolderId, setCurrentFolderId] = useState(null);

  const SESSION_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentFolderId(null);
      setSearchQuery('');
    }
  }, [isOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSessionDate = (session) => {
    return session.run_date || session.created_at || session.upload_timestamp;
  };

  // Get current folder info
  const currentFolder = useMemo(() => 
    folders.find(f => f.id === currentFolderId),
  [folders, currentFolderId]);

  // Build folder path for breadcrumb
  const folderPath = useMemo(() => {
    if (!currentFolderId) return [];
    
    const path = [];
    let current = currentFolder;
    while (current) {
      path.unshift(current);
      current = folders.find(f => f.id === current.parent_folder_id);
    }
    return path;
  }, [currentFolderId, currentFolder, folders]);

  // Get visible folders (subfolders of current folder)
  const visibleFolders = useMemo(() => {
    if (searchQuery.trim()) return []; // Hide folders when searching
    return folders.filter(f => 
      currentFolderId ? f.parent_folder_id === currentFolderId : !f.parent_folder_id
    ).sort((a, b) => a.name.localeCompare(b.name));
  }, [folders, currentFolderId, searchQuery]);

  // Get visible sessions
  const visibleSessions = useMemo(() => {
    let result = sessions;

    // Filter by folder (unless searching)
    if (!searchQuery.trim()) {
      result = result.filter(s => 
        currentFolderId ? s.folder_id === currentFolderId : !s.folder_id
      );
    }

    // Filter by search query (searches all sessions)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = sessions.filter(session => {
        const name = (session.name || session.filename || '').toLowerCase();
        const operator = (session.operator || '').toLowerCase();
        const date = formatDate(getSessionDate(session)).toLowerCase();
        return name.includes(query) || operator.includes(query) || date.includes(query);
      });
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(getSessionDate(b) || 0) - new Date(getSessionDate(a) || 0);
        case 'date_asc':
          return new Date(getSessionDate(a) || 0) - new Date(getSessionDate(b) || 0);
        case 'name':
          return (a.name || a.filename || '').localeCompare(b.name || b.filename || '');
        case 'iri':
          const iriA = sessionStats[a.session_id]?.avgIRI || a.avg_mri || 0;
          const iriB = sessionStats[b.session_id]?.avgIRI || b.avg_mri || 0;
          return iriA - iriB;
        default:
          return 0;
      }
    });

    return result;
  }, [sessions, currentFolderId, searchQuery, sortBy, sessionStats]);

  // Get selected sessions in date order for display
  const selectedSessionsOrdered = useMemo(() => {
    return [...selectedSessions]
      .map(id => sessions.find(s => s.session_id === id))
      .filter(Boolean)
      .sort((a, b) => new Date(getSessionDate(a) || 0) - new Date(getSessionDate(b) || 0));
  }, [selectedSessions, sessions]);

  const handleSelectSession = (sessionId) => {
    if (selectedSessions.includes(sessionId)) {
      onSelectedSessionsChange(selectedSessions.filter(id => id !== sessionId));
    } else if (selectedSessions.length < 4) {
      onSelectedSessionsChange([...selectedSessions, sessionId]);
    }
  };

  const handleRemoveSession = (sessionId) => {
    onSelectedSessionsChange(selectedSessions.filter(id => id !== sessionId));
  };

  const handleClearAll = () => {
    onSelectedSessionsChange([]);
  };

  const handleQuickSelect = (type) => {
    const sortedByDate = [...sessions].sort(
      (a, b) => new Date(getSessionDate(a) || 0) - new Date(getSessionDate(b) || 0)
    );

    if (type === 'first_last' && sortedByDate.length >= 2) {
      onSelectedSessionsChange([
        sortedByDate[0].session_id,
        sortedByDate[sortedByDate.length - 1].session_id
      ]);
    } else if (type === 'last_four') {
      const lastFour = sortedByDate.slice(-4).map(s => s.session_id);
      onSelectedSessionsChange(lastFour);
    }
  };

  // Get folder for a session (for search results display)
  const getSessionFolder = (session) => {
    if (!session.folder_id) return null;
    return folders.find(f => f.id === session.folder_id);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-sm shadow-2xl w-full max-w-8xl max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Select Sessions to Compare</h2>
              <p className="text-sm text-gray-500 mt-0.5">Choose up to 4 sessions • Ordered by run date</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body - Two Column Layout */}
          <div className="flex-1 flex min-h-0 overflow-hidden">
            {/* Left Panel - Session Browser */}
            <div className="w-2/3 border-r border-gray-200 flex flex-col min-w-0 overflow-hidden">
              {/* Navigation Bar */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                {/* Folder Breadcrumb */}
                <div className="flex items-center gap-1 text-sm mb-3">
                  {currentFolderId && (
                    <button
                      onClick={() => setCurrentFolderId(currentFolder?.parent_folder_id || null)}
                      className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-sm transition-colors mr-1"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => setCurrentFolderId(null)}
                    className={`px-2 py-1 rounded-sm hover:bg-gray-200 transition-colors ${
                      !currentFolderId ? 'font-medium text-gray-900 bg-white shadow-sm' : 'text-gray-600'
                    }`}
                  >
                    Root
                  </button>
                  {folderPath.map((folder) => (
                    <React.Fragment key={folder.id}>
                      <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <button
                        onClick={() => setCurrentFolderId(folder.id)}
                        className={`px-2 py-1 rounded-sm hover:bg-gray-200 transition-colors truncate max-w-[150px] ${
                          folder.id === currentFolderId ? 'font-medium text-gray-900 bg-white shadow-sm' : 'text-gray-600'
                        }`}
                        title={folder.name}
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                {/* Search + Sort Row */}
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search all sessions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="text-sm border border-gray-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="date_desc">Newest First</option>
                    <option value="date_asc">Oldest First</option>
                    <option value="name">Name (A-Z)</option>
                    <option value="iri">IRI (Low → High)</option>
                  </select>
                </div>
              </div>

              {/* Session Table */}
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="w-12 px-4 py-3 text-left"></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Distance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Run Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {/* Folders */}
                    {visibleFolders.map((folder) => {
                      const folderSessionCount = sessions.filter(s => s.folder_id === folder.id).length;
                      const subfolderCount = folders.filter(f => f.parent_folder_id === folder.id).length;
                      
                      return (
                        <tr
                          key={`folder-${folder.id}`}
                          onClick={() => setCurrentFolderId(folder.id)}
                          className="hover:bg-yellow-50 cursor-pointer group"
                        >
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <FolderIcon className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate group-hover:text-yellow-700">
                                  {folder.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {subfolderCount > 0 && `${subfolderCount} folder${subfolderCount !== 1 ? 's' : ''}, `}
                                  {folderSessionCount} session{folderSessionCount !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3">
                            <ChevronRightIcon className="w-4 h-4 text-gray-400" />
                          </td>
                        </tr>
                      );
                    })}

                    {/* Sessions */}
                    {visibleSessions.map((session) => {
                      const isSelected = selectedSessions.includes(session.session_id);
                      const canSelect = selectedSessions.length < 4 || isSelected;
                      const stats = sessionStats[session.session_id];
                      const sessionFolder = searchQuery ? getSessionFolder(session) : null;

                      return (
                        <tr
                          key={session.session_id}
                          onClick={() => canSelect && handleSelectSession(session.session_id)}
                          className={`transition-colors ${
                            isSelected 
                              ? 'bg-blue-50 hover:bg-blue-100' 
                              : canSelect 
                                ? 'hover:bg-gray-50 cursor-pointer' 
                                : 'opacity-50 cursor-not-allowed'
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div 
                              className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center transition-colors ${
                                isSelected 
                                  ? 'bg-blue-600 border-blue-600' 
                                  : 'border-gray-300 group-hover:border-gray-400'
                              }`}
                            >
                              {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <DocumentIcon className={`w-5 h-5 flex-shrink-0 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate" title={session.name || session.filename}>
                                  {session.name || session.filename || 'Unnamed Session'}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  {session.operator && (
                                    <span className="flex items-center gap-1">
                                      <UserIcon className="w-3 h-3" />
                                      {session.operator}
                                    </span>
                                  )}
                                  {sessionFolder && (
                                    <span className="flex items-center gap-1 text-yellow-600">
                                      <FolderIcon className="w-3 h-3" />
                                      {sessionFolder.name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {session.total_distance_mi 
                              ? `${session.total_distance_mi.toFixed(2)} mi`
                              : '—'
                            }
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(getSessionDate(session))}
                          </td>
                        </tr>
                      );
                    })}

                    {/* Empty State */}
                    {visibleFolders.length === 0 && visibleSessions.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-4 py-16 text-center">
                          <FolderIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm font-medium text-gray-900">
                            {searchQuery ? 'No sessions match your search' : 'No sessions in this folder'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {searchQuery ? 'Try a different search term' : 'Navigate to a different folder'}
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right Panel - Selected Sessions */}
            <div className="w-1/3 flex flex-col bg-gray-50 min-w-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Selected <span className="text-blue-600">({selectedSessions.length}/4)</span>
                  </h3>
                  {selectedSessions.length > 0 && (
                    <button
                      onClick={handleClearAll}
                      className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Station Coverage Visualization */}
              <StationCoverageVisualization
                selectedSessions={selectedSessions}
                sessions={sessions}
                sessionStats={sessionStats}
                SESSION_COLORS={SESSION_COLORS}
              />

              {/* Selected Sessions List */}
              <div className="flex-1 overflow-y-auto p-4 min-h-0">
                {selectedSessionsOrdered.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <ArrowsRightLeftIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm font-medium">No sessions selected</p>
                      <p className="text-xs mt-1">Click sessions to add them</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedSessionsOrdered.map((session, index) => {
                      const color = SESSION_COLORS[index];
                      const stats = sessionStats[session.session_id];

                      return (
                        <div
                          key={session.session_id}
                          className="bg-white rounded-sm border border-gray-200 p-3 shadow-sm"
                        >
                          <div className="flex items-start gap-3">
                            <div 
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                              style={{ backgroundColor: color }}
                            >
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p 
                                className="text-sm font-medium text-gray-900 truncate" 
                                title={session.name || session.filename}
                              >
                                {session.name || session.filename || 'Unnamed'}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {formatDate(getSessionDate(session))}
                              </p>
                              {(stats?.avgIRI > 0 || session.avg_mri > 0) && (
                                <p className="text-xs text-gray-400">
                                  {(stats?.avgIRI || session.avg_mri).toFixed(0)} in/mi avg
                                </p>
                              )}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveSession(session.session_id);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors flex-shrink-0"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Info Note */}
              {selectedSessionsOrdered.length > 0 && (
                <div className="p-3 border-t border-gray-200 bg-blue-50 flex-shrink-0">
                  <p className="text-xs text-blue-700">
                    Sessions ordered by run date (oldest → newest)
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <p className="text-sm text-gray-500">
              {selectedSessions.length === 0 && 'Select at least 2 sessions'}
              {selectedSessions.length === 1 && 'Select 1 more session'}
              {selectedSessions.length >= 2 && `${selectedSessions.length} sessions ready to compare`}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onClose}
                disabled={selectedSessions.length < 2}
                className="px-5 py-2 text-sm bg-gray-800 text-white rounded-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Compare Sessions
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPARISON TAB COMPONENT
// ============================================================================
const ComparisonTab = ({
  project,
  sessions = [],
  folders = [],
  projectConfig = null,
  onSessionDataFetch = () => Promise.resolve({}),
  embedded = false,
  selectedSessions = [],
  onSelectedSessionsChange = () => {}
}) => {
  const [sessionData, setSessionData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  
  // Table settings
  const [thresholdType, setThresholdType] = useState('constant'); // 'constant' or 'improvement'
  const [iriThreshold, setIriThreshold] = useState(70);
  const [improvementThreshold, setImprovementThreshold] = useState(15);
  const [sortConfig, setSortConfig] = useState({ key: 'index', direction: 'asc' });

  const SESSION_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

  // Fetch session data when selected sessions change
  useEffect(() => {
    const fetchSessionsData = async () => {
      if (selectedSessions.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        const newSessionData = {};
        const newLoadingStates = {};

        for (const sessionId of selectedSessions) {
          if (!sessionData[sessionId]) {
            newLoadingStates[sessionId] = true;
            setLoadingStates(prev => ({ ...prev, [sessionId]: true }));

            try {
              const data = await onSessionDataFetch(sessionId);
              newSessionData[sessionId] = data;
              newLoadingStates[sessionId] = false;
            } catch (err) {
              console.error(`Error fetching data for session ${sessionId}:`, err);
              newLoadingStates[sessionId] = false;
            }
          }
        }

        setSessionData(prev => ({ ...prev, ...newSessionData }));
        setLoadingStates(prev => ({ ...prev, ...newLoadingStates }));
      } catch (error) {
        console.error('Error fetching session data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionsData();
  }, [selectedSessions, onSessionDataFetch]);

  const formatStation = (distance) => {
    if (!distance && distance !== 0) return 'N/A';
    if (isNaN(distance)) return 'N/A';
    const hundreds = Math.floor(distance / 100);
    const remainder = distance % 100;
    return `${hundreds}+${remainder.toFixed(0).padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSessionDate = (session) => {
    return session?.run_date || session?.created_at || session?.upload_timestamp;
  };

  const getSessionInfo = (sessionId) => {
    return sessions.find(s => s.session_id === sessionId);
  };

  const sessionColorMap = useMemo(() => {
    const colorMap = {};
    const orderedIds = [...selectedSessions]
      .map(id => ({ id, session: getSessionInfo(id) }))
      .sort((a, b) => {
        const dateA = new Date(getSessionDate(a.session) || 0);
        const dateB = new Date(getSessionDate(b.session) || 0);
        return dateA - dateB;
      })
      .map(item => item.id);

    orderedIds.forEach((sessionId, index) => {
      colorMap[sessionId] = SESSION_COLORS[index % SESSION_COLORS.length];
    });
    return colorMap;
  }, [selectedSessions, sessions]);

  const getSessionColor = (sessionId) => {
    return sessionColorMap[sessionId] || '#666666';
  };

  // Calculate stats for all sessions
  const allSessionStats = useMemo(() => {
    const stats = {};

    sessions.forEach(session => {
      const sessionId = session.session_id;
      const data = sessionData[sessionId];

      if (!data?.segments) {
        stats[sessionId] = {
          totalDistance: 0,
          avgIRI: session.avg_mri || 0,
          segmentCount: 0,
          validSegmentCount: 0,
          exceptionSegmentCount: 0,
          minStation: null,
          maxStation: null,
        };
        return;
      }

      const segments = data.segments;
      const totalDistance = segments.reduce((sum, s) => sum + (s.distance_ft || 0), 0);
      
      const validIRISegments = segments.filter(s => 
        !s.exception && s.iri_avg_in_per_mi && s.iri_avg_in_per_mi > 0
      );
      const avgIRI = validIRISegments.length > 0
        ? validIRISegments.reduce((sum, s) => sum + (s.iri_avg_in_per_mi || 0), 0) / validIRISegments.length
        : 0;

      // Calculate station range
      const segmentsWithStations = segments.filter(s => 
        s.from_station_ft != null && s.to_station_ft != null
      );
      const minStation = segmentsWithStations.length > 0 
        ? Math.min(...segmentsWithStations.map(s => s.from_station_ft)) 
        : null;
      const maxStation = segmentsWithStations.length > 0 
        ? Math.max(...segmentsWithStations.map(s => s.to_station_ft)) 
        : null;

      stats[sessionId] = {
        totalDistance: totalDistance / 5280,
        avgIRI,
        segmentCount: segments.length,
        validSegmentCount: validIRISegments.length,
        exceptionSegmentCount: segments.filter(s => s.exception).length,
        minStation,
        maxStation,
      };
    });

    return stats;
  }, [sessions, sessionData]);

  // Compute comparison stats for selected sessions
  const comparisonStats = useMemo(() => {
    const stats = {};

    selectedSessions.forEach(sessionId => {
      const data = sessionData[sessionId];
      if (!data?.segments) return;

      const segments = data.segments;
      const events = data.events || [];

      const totalDistance = segments.reduce((sum, s) => sum + (s.distance_ft || 0), 0);
      
      const validIRISegments = segments.filter(s => 
        !s.exception && s.iri_avg_in_per_mi && s.iri_avg_in_per_mi > 0
      );
      const avgIRI = validIRISegments.length > 0
        ? validIRISegments.reduce((sum, s) => sum + (s.iri_avg_in_per_mi || 0), 0) / validIRISegments.length
        : 0;

      const iriThreshold_A = projectConfig?.iri_threshold_type_a || 70;

      const bumpCount = events.filter(e => e.event_type === 'Bump').length;
      const dipCount = events.filter(e => e.event_type === 'Dip').length;
      const iriThreshold_B = projectConfig?.iri_threshold_type_b || 170;
      const lrCount = events.filter(e => e.event_type === 'LR' && (e.magnitude_in || 0) > iriThreshold_B).length;

      const sessionInfo = getSessionInfo(sessionId);
      const sessionDate = getSessionDate(sessionInfo);

      stats[sessionId] = {
        totalDistance: totalDistance / 5280,
        avgIRI,
        eventCounts: { bumps: bumpCount, dips: dipCount, lr: lrCount },
        totalEvents: bumpCount + dipCount + lrCount,
        sessionInfo,
        segmentCount: segments.length,
        validSegmentCount: validIRISegments.length,
        exceptionSegmentCount: segments.filter(s => s.exception).length,
        sessionDate: sessionDate ? new Date(sessionDate) : new Date(0)
      };
    });

    const sortedSessionIds = [...selectedSessions].sort((a, b) => {
      const dateA = stats[a]?.sessionDate || new Date(0);
      const dateB = stats[b]?.sessionDate || new Date(0);
      return dateA - dateB;
    });

    const statsWithChanges = {};
    sortedSessionIds.forEach((sessionId, index) => {
      const current = stats[sessionId];
      const previousSessionId = sortedSessionIds[index - 1];
      const previous = previousSessionId ? stats[previousSessionId] : null;

      let changes = {};
      if (previous) {
        const calcChange = (currentVal, prevVal) => {
          if (currentVal == null || isNaN(currentVal) || prevVal == null || isNaN(prevVal)) return undefined;
          if (prevVal === 0) return currentVal > 0 ? 100 : 0;
          return ((currentVal - prevVal) / prevVal) * 100;
        };

        changes = {
          avgIRI: calcChange(current.avgIRI, previous.avgIRI),
          bumps: calcChange(current.eventCounts.bumps, previous.eventCounts.bumps),
          dips: calcChange(current.eventCounts.dips, previous.eventCounts.dips),
          lr: calcChange(current.eventCounts.lr, previous.eventCounts.lr)
        };
      }

      statsWithChanges[sessionId] = { ...current, changes, isFirstSession: !previous };
    });

    return { stats: statsWithChanges, orderedSessions: sortedSessionIds };
  }, [selectedSessions, sessionData, projectConfig, sessions]);

  const statsData = comparisonStats?.stats || {};
  const orderedSessions = comparisonStats?.orderedSessions || [];

  // Build segment comparison table data
  const segmentTableData = useMemo(() => {
    if (orderedSessions.length < 2) return { rows: [], alignmentWarnings: [], summary: null };

    const baselineSessionId = orderedSessions[0];
    const finalSessionId = orderedSessions[orderedSessions.length - 1];
    const baselineData = sessionData[baselineSessionId];

    if (!baselineData?.segments) return { rows: [], alignmentWarnings: [], summary: null };

    // Get baseline segments (excluding exceptions)
    const baselineSegments = baselineData.segments
      .filter(s => !s.exception && s.iri_avg_in_per_mi != null)
      .sort((a, b) => a.from_station_ft - b.from_station_ft);

    if (baselineSegments.length === 0) return { rows: [], alignmentWarnings: [], summary: null };

    // Track alignment warnings per session
    const alignmentWarnings = [];

    // Build rows from baseline segments
    const rows = baselineSegments.map((baseSeg, index) => {
      const row = {
        index: index + 1,
        from: baseSeg.from_station_ft,
        to: baseSeg.to_station_ft,
        length: baseSeg.distance_ft || (baseSeg.to_station_ft - baseSeg.from_station_ft),
        sessions: {}
      };

      // Add baseline IRI
      row.sessions[baselineSessionId] = baseSeg.iri_avg_in_per_mi;

      // Match segments from other sessions
      orderedSessions.slice(1).forEach(sessionId => {
        const otherData = sessionData[sessionId];
        if (!otherData?.segments) {
          row.sessions[sessionId] = null;
          return;
        }

        // Find exact matching segment
        const matchingSeg = otherData.segments.find(s => 
          !s.exception &&
          s.from_station_ft === baseSeg.from_station_ft &&
          s.to_station_ft === baseSeg.to_station_ft
        );

        row.sessions[sessionId] = matchingSeg?.iri_avg_in_per_mi ?? null;
      });

      // Calculate percent change between each consecutive session
      row.changes = {};
      for (let i = 1; i < orderedSessions.length; i++) {
        const prevSessionId = orderedSessions[i - 1];
        const currSessionId = orderedSessions[i];
        const prevIRI = row.sessions[prevSessionId];
        const currIRI = row.sessions[currSessionId];
        
        if (prevIRI != null && currIRI != null && prevIRI !== 0) {
          row.changes[currSessionId] = ((currIRI - prevIRI) / prevIRI) * 100;
        } else {
          row.changes[currSessionId] = null;
        }
      }

      // Calculate total change (baseline to final) - negative means IRI decreased (good)
      const baselineIRI = row.sessions[baselineSessionId];
      const finalIRI = row.sessions[finalSessionId];
      if (baselineIRI != null && finalIRI != null && baselineIRI !== 0) {
        row.totalChange = ((finalIRI - baselineIRI) / baselineIRI) * 100;
      } else {
        row.totalChange = null;
      }

      return row;
    });

    // Check for alignment warnings
    orderedSessions.slice(1).forEach(sessionId => {
      const otherData = sessionData[sessionId];
      if (!otherData?.segments) return;

      const missingCount = rows.filter(r => r.sessions[sessionId] == null).length;
      if (missingCount > 0) {
        const sessionInfo = getSessionInfo(sessionId);
        alignmentWarnings.push({
          sessionId,
          sessionName: sessionInfo?.name || 'Session',
          missingCount
        });
      }
    });

    // Calculate summary stats
    const validRows = rows.filter(r => r.sessions[baselineSessionId] != null && r.sessions[finalSessionId] != null);
    const totalDistance = validRows.reduce((sum, r) => sum + (r.length || 0), 0);
    const baselineAvg = validRows.length > 0 
      ? validRows.reduce((sum, r) => sum + r.sessions[baselineSessionId], 0) / validRows.length 
      : 0;
    const finalAvg = validRows.length > 0
      ? validRows.reduce((sum, r) => sum + r.sessions[finalSessionId], 0) / validRows.length
      : 0;
    const avgChange = baselineAvg !== 0 ? ((finalAvg - baselineAvg) / baselineAvg) * 100 : 0;

    return {
      rows,
      alignmentWarnings,
      summary: {
        segmentCount: rows.length,
        totalDistance,
        baselineAvg,
        finalAvg,
        avgChange
      }
    };
  }, [orderedSessions, sessionData, sessions]);

  // Sort rows
  const sortedRows = useMemo(() => {
    if (!segmentTableData.rows.length) return [];

    const sorted = [...segmentTableData.rows];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'index':
          aVal = a.index;
          bVal = b.index;
          break;
        case 'from':
          aVal = a.from;
          bVal = b.from;
          break;
        default:
          aVal = a.index;
          bVal = b.index;
      }

      if (sortConfig.direction === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });

    return sorted;
  }, [segmentTableData.rows, sortConfig]);

  // Export CSV
  const handleExportCSV = useCallback(() => {
    if (!segmentTableData.rows.length) return;

    const headers = ['#', 'From', 'To', 'Length'];
    orderedSessions.forEach((sessionId, index) => {
      const info = getSessionInfo(sessionId);
      headers.push(info?.name || 'Session');
      if (index < orderedSessions.length - 1) {
        headers.push('% Change');
      }
    });
    headers.push('Total %');

    const csvRows = [headers.join(',')];
    
    segmentTableData.rows.forEach(row => {
      const values = [
        row.index,
        formatStation(row.from),
        formatStation(row.to),
        row.length
      ];
      orderedSessions.forEach((sessionId, index) => {
        values.push(row.sessions[sessionId] ?? '');
        if (index < orderedSessions.length - 1) {
          const nextSessionId = orderedSessions[index + 1];
          values.push(row.changes[nextSessionId]?.toFixed(1) ?? '');
        }
      });
      values.push(row.totalChange?.toFixed(1) ?? '');
      csvRows.push(values.join(','));
    });

    const csv = csvRows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `segment-comparison-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [segmentTableData.rows, orderedSessions, sessions]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleRemoveSession = (sessionId) => {
    onSelectedSessionsChange(selectedSessions.filter(id => id !== sessionId));
  };

  const getChangeIndicator = (change) => {
    if (change == null || isNaN(change) || Math.abs(change) < 0.1) return null;

    const isGood = change < 0;
    return (
      <span className={`inline-flex items-center text-xs font-medium ${isGood ? 'text-green-600' : 'text-red-600'}`}>
        {isGood ? <ArrowTrendingDownIcon className="w-3 h-3 mr-0.5" /> : <ArrowTrendingUpIcon className="w-3 h-3 mr-0.5" />}
        {Math.abs(change).toFixed(1)}%
      </span>
    );
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-sm p-4">
        <p className="text-sm text-red-800">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-full">
      {/* Session Selection Modal */}
      <SessionSelectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        sessions={sessions}
        folders={folders}
        selectedSessions={selectedSessions}
        onSelectedSessionsChange={onSelectedSessionsChange}
        sessionStats={allSessionStats}
      />

      {/* Header */}
      <div className="h-14 bg-gray-50 px-6 border-b border-gray-200 flex items-center">
        <div className="w-full flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Session Comparison</h3>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Select Sessions
            <span className="bg-red-700 px-1.5 py-0.5 rounded text-xs">{selectedSessions.length}/4</span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200">
          <p className="text-xs text-red-800">Error: {error}</p>
        </div>
      )}

      {/* Empty State */}
      {selectedSessions.length === 0 && (
        <div className="px-6 py-16">
          <div className="text-center max-w-sm mx-auto">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <ChartBarIcon className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900">No sessions selected</h3>
            <p className="text-xs text-gray-500 mt-1">Select sessions to compare their IRI profiles.</p>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Select Sessions
            </button>
          </div>
        </div>
      )}

      {/* Session Summary Cards */}
      {selectedSessions.length > 0 && (
        <div className="border-b border-gray-200 px-4 py-3">
          <div className="flex gap-3 overflow-x-auto">
            {orderedSessions.map((sessionId, index) => {
              const info = getSessionInfo(sessionId);
              const color = getSessionColor(sessionId);
              const stats = statsData[sessionId];
              const isLoading = loadingStates[sessionId];
              const nextStats = index < orderedSessions.length - 1 ? statsData[orderedSessions[index + 1]] : null;
              const mriChange = nextStats?.changes?.avgIRI;

              return (
                <React.Fragment key={sessionId}>
                  <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-sm font-medium text-gray-900 truncate">{info?.name || 'Session'}</span>
                      </div>
                      <button onClick={() => handleRemoveSession(sessionId)} className="p-1 hover:bg-gray-100 rounded shrink-0">
                        <XMarkIcon className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>
                    <div className="text-xs text-gray-400 mb-2.5">{formatDate(getSessionDate(info))}</div>
                    {isLoading ? (
                      <div className="space-y-2">
                        <div className="h-3.5 w-20 bg-gray-200 rounded animate-pulse" />
                        <div className="h-3.5 w-16 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ) : (() => {
                      const changes = stats?.changes || {};
                      const prevStats = index > 0 ? statsData[orderedSessions[index - 1]] : null;
                      const ch = (curr, prev) => {
                        if (curr == null || prev == null || prev === 0) return null;
                        return ((curr - prev) / prev) * 100;
                      };
                      const mriCh = !stats?.isFirstSession ? changes.avgIRI : null;
                      const bumpCh = !stats?.isFirstSession ? changes.bumps : null;
                      const dipCh = !stats?.isFirstSession ? changes.dips : null;
                      const lrCh = !stats?.isFirstSession ? changes.lr : null;
                      const renderChange = (val) => {
                        if (val == null || Math.abs(val) < 0.1) return null;
                        const good = val < 0;
                        return <span className={`text-xs font-semibold ${good ? 'text-green-600' : 'text-red-600'}`}>{val > 0 ? '+' : ''}{val.toFixed(0)}%</span>;
                      };
                      return (
                        <div className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
                          <div>
                            <div className="text-gray-400">MRI</div>
                            <div className="flex items-baseline gap-1">
                              <span className="font-semibold text-gray-800 font-mono tabular-nums">{stats?.avgIRI > 0 ? stats.avgIRI.toFixed(1) : '—'}</span>
                              {renderChange(mriCh)}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-400">Dist</div>
                            <div className="font-medium text-gray-700 font-mono tabular-nums">{stats?.totalDistance?.toFixed(2) || '—'} mi</div>
                          </div>
                          <div>
                            <div className="text-gray-400">Segs</div>
                            <div className="font-medium text-gray-700 font-mono tabular-nums">{stats?.segmentCount ?? '—'}</div>
                          </div>
                          <div>
                            <div className="text-gray-400">Bumps</div>
                            <div className="flex items-baseline gap-1">
                              <span className="font-medium text-gray-700 font-mono tabular-nums">{stats?.eventCounts?.bumps ?? 0}</span>
                              {renderChange(bumpCh)}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-400">Dips</div>
                            <div className="flex items-baseline gap-1">
                              <span className="font-medium text-gray-700 font-mono tabular-nums">{stats?.eventCounts?.dips ?? 0}</span>
                              {renderChange(dipCh)}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-400">ALRs</div>
                            <div className="flex items-baseline gap-1">
                              <span className="font-medium text-gray-700 font-mono tabular-nums">{stats?.eventCounts?.lr ?? 0}</span>
                              {renderChange(lrCh)}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* IRI Overlay Chart */}
      {orderedSessions.length >= 2 && (
        <ComparisonChart
          orderedSessions={orderedSessions}
          sessionData={sessionData}
          sessions={sessions}
          getSessionInfo={getSessionInfo}
          getSessionColor={getSessionColor}
          getSessionDate={getSessionDate}
          formatStation={formatStation}
          formatDate={formatDate}
          SESSION_COLORS={SESSION_COLORS}
          projectConfig={projectConfig}
          iriThreshold={iriThreshold}
          thresholdType={thresholdType}
        />
      )}

      {/* Segment Comparison Table */}
      {orderedSessions.length >= 2 && sortedRows.length > 0 && (
        <div className="flex flex-col flex-1 min-h-0">
          {/* Section Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold text-gray-900">Segment Comparison</h3>
              {segmentTableData.alignmentWarnings.length > 0 && (
                <div className="relative group">
                  <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 cursor-help" />
                  <div className="absolute left-0 top-6 z-20 hidden group-hover:block w-64 p-2 bg-amber-50 border border-amber-200 rounded shadow-lg text-xs text-amber-800">
                    {segmentTableData.alignmentWarnings.map((w) => (
                      <div key={w.sessionId}>"{w.sessionName}" — {w.missingCount} unaligned segments</div>
                    ))}
                  </div>
                </div>
              )}

              <div className="w-px h-5 bg-gray-300" />

              {/* Threshold controls inline */}
              <div className="flex items-center gap-2">
                <select
                  value={thresholdType}
                  onChange={(e) => setThresholdType(e.target.value)}
                  className="px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                >
                  <option value="constant">MRI Limit</option>
                  <option value="improvement">% Improvement</option>
                </select>
                <input
                  type="number"
                  value={thresholdType === 'constant' ? iriThreshold : improvementThreshold}
                  onChange={(e) => thresholdType === 'constant' ? setIriThreshold(Number(e.target.value) || 0) : setImprovementThreshold(Number(e.target.value) || 0)}
                  className="w-16 px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-red-500"
                />
                <span className="text-xs text-gray-500">{thresholdType === 'constant' ? 'in/mi' : '%'}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Summary stats */}
              {segmentTableData.summary && (
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>{segmentTableData.summary.segmentCount} segs</span>
                  <span>Base avg <strong className="text-gray-700">{segmentTableData.summary.baselineAvg.toFixed(0)}</strong></span>
                  <span>Final avg <strong className="text-gray-700">{segmentTableData.summary.finalAvg.toFixed(0)}</strong></span>
                  <span className={segmentTableData.summary.avgChange < 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                    {segmentTableData.summary.avgChange > 0 ? '+' : ''}{segmentTableData.summary.avgChange.toFixed(0)}%
                  </span>
                </div>
              )}

              <button
                onClick={handleExportCSV}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                CSV
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 border-b border-gray-200">
                <tr>
                  <th onClick={() => handleSort('index')} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 w-[1%]">#</th>
                  <th onClick={() => handleSort('from')} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 w-[1%] whitespace-nowrap">From</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[1%] whitespace-nowrap">To</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[1%]">Len</th>
                  {orderedSessions.map((sessionId, index) => {
                    const color = getSessionColor(sessionId);
                    const info = getSessionInfo(sessionId);
                    const shortDate = info ? new Date(getSessionDate(info)).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) : '';
                    return (
                      <React.Fragment key={sessionId}>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[1%]">
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                            <span>{index === 0 ? 'Base' : `R${index + 1}`}</span>
                            <span className="text-gray-400 font-normal">{shortDate}</span>
                          </div>
                        </th>
                        {index < orderedSessions.length - 1 && (
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase w-[1%]">%Δ</th>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-gray-100 w-[1%]">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedRows.map((row) => {
                  const baselineIRI = row.sessions[orderedSessions[0]];
                  return (
                    <tr key={row.index} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-400 tabular-nums">{row.index}</td>
                      <td className="px-3 py-2.5 text-gray-800 font-mono tabular-nums">{formatStation(row.from)}</td>
                      <td className="px-3 py-2.5 text-gray-800 font-mono tabular-nums">{formatStation(row.to)}</td>
                      <td className="px-3 py-2.5 text-gray-500 font-mono tabular-nums">{row.length}'</td>
                      {orderedSessions.map((sessionId, index) => {
                        const iri = row.sessions[sessionId];
                        const nextSessionId = index < orderedSessions.length - 1 ? orderedSessions[index + 1] : null;
                        const nextIRI = nextSessionId ? row.sessions[nextSessionId] : null;
                        const changePct = iri != null && nextIRI != null && iri !== 0 ? ((nextIRI - iri) / iri) * 100 : null;

                        return (
                          <React.Fragment key={sessionId}>
                            <td className="px-3 py-2.5 font-mono tabular-nums">
                              {iri != null ? (
                                <span className="font-medium text-gray-800">{iri.toFixed(0)}</span>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </td>
                            {index < orderedSessions.length - 1 && (
                              <td className={`px-3 py-2.5 text-xs font-semibold tabular-nums ${
                                changePct == null ? 'text-gray-300' : changePct < 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {changePct != null && Math.abs(changePct) >= 0.5
                                  ? (changePct > 0 ? '+' : '') + changePct.toFixed(0) + '%'
                                  : '—'}
                              </td>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <td className={`px-3 py-2.5 bg-gray-50 font-medium font-mono tabular-nums ${
                        row.totalChange == null ? 'text-gray-300' : row.totalChange < 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {row.totalChange != null ? (row.totalChange > 0 ? '+' : '') + row.totalChange.toFixed(0) + '%' : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedSessions.length === 1 && (
        <div className="px-4 py-3 bg-blue-50 border-t border-blue-200">
          <p className="text-xs text-blue-800">Select at least one more session to see the comparison.</p>
        </div>
      )}
    </div>
  );
};

export default ComparisonTab;
